import { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import DosageSelector from '../../components/product/DosageSelector';
import PaymentDeclineModal from '../../components/modals/PaymentDeclineModal';
import SubscriptionClauseModal from '../../components/modals/SubscriptionClauseModal';
import AchPromotionModal from '../../components/modals/AchPromotionModal';
import api from '../../services/api';
import { getSupplyLabel, getSupplyTerm, resolveBasisSupplyMonths, toPerMonthRate } from '../../utils/supplyTerms';
import {
  ASYNC_REVIEW_NOTICE,
  APPROVAL_UPDATE_NOTICE,
  CONTACT_IF_QUESTIONS_NOTICE,
  NO_APPOINTMENT_NEEDED_NOTICE,
  shouldOfferScheduling
} from '../../utils/asyncConsultCopy';
import { derivePlaceOrderBlockers } from '../../utils/placeOrderBlockers';
import { deriveIsStripeArm, deriveCardEntryError, buildCardPaymentPayload } from '../../utils/checkoutPaymentArm';
import { REQUEST_TIMEOUT_CODE, REQUEST_TIMEOUT_MESSAGE } from '../../utils/requestTimeout';
import { DOCTOR_NOW_DOSAGE_NOTICE, canCustomerSelectDosage } from '../../utils/doctorNowDosageNotice';

import styles from './CheckoutPage.module.css';

/**
 * Card #53493 — checkout card entry is routed by the payment switch.
 *
 * 🔑 THE PROVIDER COMPONENT IS LAZY, AND THAT IS A REQUIREMENT, NOT A SIZE OPTIMISATION.
 * A static import puts the provider SDK in the main chunk of EVERY build, including one made
 * with the switch off, which would make the OFF bundle reach the provider host on load — the
 * exact posture #53076's reachability census asserts against. `PaymentForm.jsx` states the
 * same rule for the profile surface; this is the same rule for checkout.
 */
const StripePaymentElement = lazy(() => import('../../components/forms/StripePaymentElement'));

// Legal terms version — single source of truth (must match api/config/constants.js)
const TERMS_VERSION = '2026-04-02';

/**
 * Card #54415 (D3). The submit POST gets a wall-clock budget, because a hung request was the ONE
 * path where a member could not tell whether they had paid: `loading` stayed true forever, the
 * button read "Processing..." and `submittingRef` silently dropped every further click.
 *
 * 45 s is deliberately generous — it must not fire on a slow-but-successful authorisation — while
 * still being finite. A retry afterward is SAFE: the server's 5-minute duplicate window answers
 * `DUPLICATE_ORDER` instead of charging twice.
 * ROLLBACK: delete the `{ timeoutMs }` argument at the `/checkout/submit` call.
 */
const CHECKOUT_SUBMIT_TIMEOUT_MS = 45000;

/**
 * Card 54409 — the idempotency identity of ONE checkout submission.
 *
 * Minted once per submission and reused by every retry of it, so the server can derive a
 * provider idempotency key that collapses a retried POST onto the authorization the first
 * attempt placed. It is deliberately kept across a TRANSPORT failure (no response: we do not
 * know whether the first request reached the server) and discarded after a definitive server
 * refusal, because the next attempt is a new order and the provider caches a declined key
 * under the old one for 24 hours.
 */
const createSubmissionId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // randomUUID is unavailable on insecure origins; uniqueness is all this id needs.
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

// Place Order's blocker list lives in web/utils/placeOrderBlockers.js — a pure derivation, so
// it can be exercised directly by tests (jest here cannot import a .jsx page: `transform: {}`,
// `moduleFileExtensions: ['js','mjs','json']`). Behaviour is unchanged; see that file for why
// `pricing` is a blocker in its own right and not folded into `pricingError`.

const CheckoutPage = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isDemo } = useAuth();
  const { toast } = useToast();
  const location = useLocation();
  const appointmentTime = location.state?.appointmentTime || null;

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(true);
  const [paymentError, setPaymentError] = useState(null);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [showHoldInfoModal, setShowHoldInfoModal] = useState(false);

  // Subscription clause state — signing happens exclusively inside SubscriptionClauseModal
  // (13 acknowledgments + agreement + signature); subscriptionConsentData is the modal's
  // completed acceptance payload and is required for every supply term (60 Day = 2 months)
  const [subscriptionClauseAgreed, setSubscriptionClauseAgreed] = useState(false);
  const [subscriptionConsentData, setSubscriptionConsentData] = useState(null);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [membershipTier, setMembershipTier] = useState(null);
  // Last captured signature — restored into the modal when it reopens
  const [sharedSignatureData, setSharedSignatureData] = useState(null);


  // Product and pricing state
  const [product, setProduct] = useState(null);
  const [dosage, setDosage] = useState(null);
  const [selectedSupplyMonths, setSelectedSupplyMonths] = useState(3); // Default to 3-month supply
  const [pricing, setPricing] = useState(null);
  const [pricingBreakdown, setPricingBreakdown] = useState(null);
  const [pricingError, setPricingError] = useState(null);
  const [quantity, setQuantity] = useState(1);

  // Higher-dosage refill metadata (Path 2) — written by the Orders disclaimer modal
  const [higherDosagePayload] = useState(() => {
    try {
      const parsed = JSON.parse(sessionStorage.getItem('oval_higher_dosage_request'));
      return parsed?.higherDosageRequest && parsed?.originalOrderId
        ? {
            originalOrderId: parsed.originalOrderId,
            originalProduct: parsed.originalProduct ?? null,
            originalDosage: parsed.originalDosage ?? null
          }
        : null;
    } catch {
      return null;
    }
  });
  const [telehealthConsentAgreed, setTelehealthConsentAgreed] = useState(false);
  const [legalConsentAgreed, setLegalConsentAgreed] = useState(false);

  // Super offer tracking
  const [superOfferId, setSuperOfferId] = useState(null);

  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoValidation, setPromoValidation] = useState({ status: 'idle', data: null });
  const [promoRecalcTrigger, setPromoRecalcTrigger] = useState(0);
  const validatedPromoRef = useRef(null);
  const initialLoadDone = useRef(false);
  const recalcSkipRef = useRef(true);
  const promoDebounceRef = useRef(null);
  const submittingRef = useRef(false);
  // Card 54409 — survives across attempts of the SAME submission; reset by the catch below.
  const submissionIdRef = useRef(null);

  // Billing address (also used as shipping if sameAsShipping is true)
  const [billingData, setBillingData] = useState({
    firstName: '',
    lastName: '',
    street: '',
    street2: '',
    city: '',
    state: '',
    zipCode: '',
    phone: ''
  });

  const [sameAsShipping, setSameAsShipping] = useState(true);

  const [shippingData, setShippingData] = useState({
    firstName: '',
    lastName: '',
    street: '',
    street2: '',
    city: '',
    state: '',
    zipCode: '',
    phone: ''
  });

  // Card state - NEVER log or store full card numbers
  const [cardData, setCardData] = useState({
    number: '',
    expiration: '',
    cvv: '',
    nameOnCard: ''
  });

  // ── Card #53493. Which card surface checkout renders is the SWITCH's answer, never ours ──
  //
  // ⚠️ FAILS CLOSED TO THE INCUMBENT, exactly as `PaymentForm.jsx` does: the state starts null
  // and ANY error leaves it null, so a failed config request renders the raw-card form that has
  // always worked rather than blanking the surface a customer is trying to pay with.
  //
  // ⛔ Deliberately NOT a second read of an env var or a `provider` prop. `paymentMethodController`
  // owns this answer, and a client that re-derived it would drift from the server guard that
  // refuses a PAN (`rawCardIntake.checkRawCardIntake`) — the switch and its consumers disagreeing
  // is precisely how a customer gets a 400 they cannot act on.
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [paymentConfigAttempt, setPaymentConfigAttempt] = useState(0);
  const [paymentMethodId, setPaymentMethodId] = useState(null);
  // Card #54400 (D6): the bank method the provider's Element linked, on the hosted arm only.
  const [achPaymentMethodId, setAchPaymentMethodId] = useState(null);

  // Card #54415. Re-run on demand so the named error state below is RECOVERABLE rather than a
  // dead end: the retry re-asks the server whether the switch/key is still unusable.
  const reloadPaymentConfig = () => setPaymentConfigAttempt((attempt) => attempt + 1);

  useEffect(() => {
    let cancelled = false;
    api.get('/users/me/payment-method/config')
      .then((config) => { if (!cancelled) setPaymentConfig(config); })
      .catch(() => { if (!cancelled) setPaymentConfig(null); });
    return () => { cancelled = true; };
  }, [paymentConfigAttempt]);

  // ⛔ `defaultProvider`, ⛔ NEVER `provider` — they answer different questions and MEASURED
  // DIFFERENTLY on this box. `provider` tracks STRIPE_ENABLED ("can we SAVE a card?");
  // `defaultProvider` tracks resolveDefaultProvider ("who will TAKE the payment?"). Sandbox
  // ships `STRIPE_ENABLED=true` with `PAYMENT_PROVIDER_DEFAULT=authorize_net`, where routing on
  // `provider` hid the raw fields on the incumbent arm and earned a 400 `card is required`.
  // It is also the field the SERVER's own raw-card refusal keys off, so the client and the
  // guard cannot disagree about which body is legal.
  // ⛔ BOTH conditions — a provider alone would mount an Element with no key. Absent field ⇒
  // false ⇒ the incumbent form, which is the safe direction.
  const isStripeArm = deriveIsStripeArm(paymentConfig);
  // HSA/FSA CARDS (OV-9879, 2026-10-29). The acquirer this checkout was cut over to refuses FSA
  // cards; the incumbent's MID accepts them (Zen Payments confirmed the MCC on 2026-06-01).
  // Ticking the box switches THIS order to the incumbent — the card form (the incumbent needs the
  // PAN, the hosted arm never has it) and the merchant, since `payWithFsaCard` travels with the
  // submit. ⛔ ACH is untouched: it stays on the hosted arm.
  const [payWithFsaCard, setPayWithFsaCard] = useState(false);
  const useHostedCardForm = isStripeArm && !payWithFsaCard;

  // Card #54415 (D3). The SWITCH says the money path is the hosted provider but its publishable
  // key is a placeholder ⇒ neither surface is usable: the Element would hang on
  // "Preparing secure card entry..." (the key it was handed is rejected), and the raw-card form
  // would be refused by the server's own guard, which is routing to that same provider. So the
  // page names the state instead of hanging or silently downgrading. `null` = nothing wrong.
  const cardEntryError = deriveCardEntryError(paymentConfig);

  // Payment method state (card or ach)
  const [paymentMethod, setPaymentMethod] = useState("card");
  // ACH state
  const [achData, setAchData] = useState({
    bankName: "",
    routingNumber: "",
    accountNumber: "",
    accountType: "checking"
  });

  // ACH promo modal state
  const [showAchPromoModal, setShowAchPromoModal] = useState(false);
  const [hasAchOnFile, setHasAchOnFile] = useState(false);
  const [hasUsedAchPromo, setHasUsedAchPromo] = useState(false);
  const [isFirstOrder, setIsFirstOrder] = useState(true);
  const [achPromoModalShown, setAchPromoModalShown] = useState(false);
  const [achPromoApplied, setAchPromoApplied] = useState(false);

  // Account credit state — when applied, backend re-prices the total (Task 7).
  // creditBalanceCents/appliedCreditsCents are read directly off `pricing`.
  const [applyCredit, setApplyCredit] = useState(false);

  // Clean up promo debounce timer on unmount
  useEffect(() => () => clearTimeout(promoDebounceRef.current), []);

  // Fetch ACH status and order history for promo eligibility
  useEffect(() => {
    const fetchAchPromoEligibility = async () => {
      try {
        // Fetch ACH status (has method on file + promo usage)
        const achStatusRes = await api.get('/ach/status');
        const achStatus = achStatusRes.data?.data || achStatusRes.data || {};
        setHasAchOnFile(achStatus.hasAchOnFile || false);
        setHasUsedAchPromo(achStatus.hasUsedAchPromo || false);
        
        // Fetch order history to check if first order
        const ordersRes = await api.get('/orders/my');
        const orders = ordersRes.data?.orders || ordersRes.data?.data || ordersRes.data || [];
        setIsFirstOrder(Array.isArray(orders) ? orders.length === 0 : true);
      } catch (error) {
        // If not authenticated or API fails, assume defaults
        console.error('Failed to fetch ACH promo eligibility:', error);
      }
    };
    
    if (user) {
      fetchAchPromoEligibility();
    }
  }, [user]);

  // Handle card number focus - show ACH promo modal if eligible
  const handleCardNumberFocus = () => {
    // Only show once per session
    if (achPromoModalShown) return;
    
    // Check eligibility:
    // 1. User does NOT have ACH on file
    // 2. User has NOT used ACH promo before
    // 3. This is a first-time order
    const isEligible = !hasAchOnFile && !hasUsedAchPromo && isFirstOrder;
    
    if (isEligible) {
      // setShowAchPromoModal(true); // DISABLED - ACH coming soon
      setAchPromoModalShown(true);
    }
  };

  // Fetch membership tiers on mount
  useEffect(() => {
    const fetchMembershipTiers = async () => {
      try {
        const response = await api.get('/membership-tiers');
        if (response.tiers && response.tiers.length > 0) {
          // Use first active tier as default (price is in cents, convert to dollars).
          // Exact, not ceil'd — this is the fallback for the same value the live pricing path
          // supplies as `monthlyRateCents / 100`, and the two must not disagree by a dollar.
          const tier = response.tiers[0];
          setMembershipTier({
            name: tier.name,
            monthlyPrice: tier.price / 100
          });
        }
      } catch (error) {
        console.error('Failed to fetch membership tiers:', error);
      }
    };
    fetchMembershipTiers();
  }, []);
  // Auto-populate billing/shipping data from user profile
  useEffect(() => {
    if (user?.profile) {
      const profile = user.profile;
      const address = profile.address || {};
      setBillingData(prev => ({
        firstName: prev.firstName || profile.firstName || '',
        lastName: prev.lastName || profile.lastName || '',
        street: prev.street || address.street || '',
        street2: prev.street2 || address.street2 || '',
        city: prev.city || address.city || '',
        state: prev.state || address.state || '',
        zipCode: prev.zipCode || address.zipCode || '',
        phone: prev.phone || profile.phone || ''
      }));
      setShippingData(prev => ({
        firstName: prev.firstName || profile.firstName || '',
        lastName: prev.lastName || profile.lastName || '',
        street: prev.street || address.street || '',
        street2: prev.street2 || address.street2 || '',
        city: prev.city || address.city || '',
        state: prev.state || address.state || '',
        zipCode: prev.zipCode || address.zipCode || '',
        phone: prev.phone || profile.phone || ''
      }));
    }
  }, [user?.profile]);

  // Changing the supply term voids a prior signature — the agreement's commitment length
  // and worked example are term-specific, so the member must re-sign for the new term
  useEffect(() => {
    setSubscriptionClauseAgreed(false);
    setSubscriptionConsentData(null);
  }, [selectedSupplyMonths]);


  // Auto-populate promo code from user profile if available (wait for product to load)
  useEffect(() => {
    if (user?.appliedPromoCode && !promoCode && product?.id) {
      setPromoCode(user.appliedPromoCode);
      validatePromoCode(user.appliedPromoCode);
    }
  }, [user?.appliedPromoCode, product?.id]);

  const validatePromoCode = (code) => {
    if (promoDebounceRef.current) clearTimeout(promoDebounceRef.current);

    if (!code.trim()) {
      setPromoValidation({ status: 'idle', data: null });
      return;
    }

    setPromoValidation({ status: 'validating', data: null });

    promoDebounceRef.current = setTimeout(async () => {
      try {
        const result = await api.post('/promo-codes/validate', { code: code.trim() });
        setPromoValidation({ status: 'valid', data: result.promoCode });
        validatedPromoRef.current = code;
        // Directly recalculate pricing with the validated promo code
        try {
          const promoResult = await api.post('/checkout', {
            productId: product?.id,
            dosageId: dosage?.id,
            quantity: selectedSupplyMonths || quantity,
            promoCode: code,
            billingAddress: (billingData.street && billingData.city && billingData.zipCode) ? billingData : { firstName: 'Temp', lastName: 'User', street: '123 Main St', city: 'Austin', state: 'TX', zipCode: '78701' },
            sameAsShipping: true,
            creditsToApply: applyCredit ? (pricing?.creditBalanceCents ?? 0) : 0,
            ...(higherDosagePayload ? { higherDosage: higherDosagePayload } : {})
          });
          if (promoResult?.checkout?.pricing) {
            setPricing(promoResult.checkout.pricing);
            if (promoResult.checkout.pricingBreakdown) setPricingBreakdown(promoResult.checkout.pricingBreakdown);
            setPricingError(null);
          }
        } catch (promoErr) {
          // Last line of defence: recalculatePricing is keyed on
          // [quantity, product?.id, dosage?.id, applyCredit], so it does NOT re-fire on
          // promo validation and cannot repair the stale total. Reset to 'idle', not
          // 'invalid' — the code IS valid, only its pricing failed.
          console.error('[Checkout] Promo pricing recalc failed:', promoErr);
          setPromoValidation({ status: 'idle', data: null });
          validatedPromoRef.current = null;
          setPricingError('Promo code could not be applied — try again or continue without it.');
        }
      } catch {
        setPromoValidation({ status: 'invalid', data: null });
        validatedPromoRef.current = null;
      }
    }, 500);
  };

  const handlePromoChange = (e) => {
    const value = e.target.value;
    setPromoCode(value);
    validatePromoCode(value);
  };

  // Load product and dosage from URL params or sessionStorage
  useEffect(() => {
    const loadCheckoutData = async () => {
      if (initialLoadDone.current) return;
      setLoadingProduct(true);
      let loadedProductRes = null;
      try {
        // Get dosage ID from URL params
        const dosageId = searchParams.get('dosage');
        const urlSupplyMonths = searchParams.get('supplyMonths');
        if (urlSupplyMonths) {
          { const m = Math.min(Math.max(parseInt(urlSupplyMonths, 10) || 1, 1), 3); setSelectedSupplyMonths(m); setQuantity(m); }
        }

        // Check for super offer ID from URL
        const offerSlug = searchParams.get("offer");
        if (offerSlug) {
          try {
            const offerRes = await api.get("/offers/" + offerSlug);
            if (offerRes?.offer?.id) setSuperOfferId(offerRes.offer.id);
          } catch (e) { /* unknown or inactive offer slug - checkout proceeds unattributed */ }
        }

        // Also check sessionStorage for backup
        let storedDosage = null;
        const storedDosageData = sessionStorage.getItem('selectedDosage');
        if (storedDosageData) {
          try {
            storedDosage = JSON.parse(storedDosageData);
          } catch (e) {
            // Ignore parse errors
          }
        }

        // Get product ID from URL or stored data
        let productId = searchParams.get('productId');
        const productSlug = slug || searchParams.get('product');

        // The dosage the summary will actually price. Seeded with what was requested, then replaced
        // by whatever we resolve below — the initial pricing call must never ask the API for a
        // dosage the page isn't showing (a stale id used to 400 and leave the summary at $0.00).
        let resolvedDosageId = dosageId || storedDosage?.dosageId || null;

        // If we have a slug, load the product
        if (productSlug) {
          const productRes = await api.get(`/products/${productSlug}`);
          loadedProductRes = productRes;
          if (productRes.product) {
            setProduct(productRes.product);
            productId = productRes.product.id;

            // Resolve the dosage: requested id (URL or stored selection) → default active → first
            // active. The fallback has to fire whenever the requested id fails to RESOLVE — not
            // just when no id was asked for. A stale or hand-typed `?dosage=` that matches nothing
            // used to leave `dosage` null (the old guard was `!dosageId`, which is false in exactly
            // that case), and the order summary below then white-screened on it.
            // An explicitly-requested dosage is honoured even if inactive; only the fallback filters.
            const allDosages = productRes.product.dosages || [];
            const activeDosages = allDosages.filter(d => d.isActive !== false);
            const resolvedDosage = allDosages.find(d => d.id === resolvedDosageId)
              || activeDosages.find(d => d.isDefault)
              || activeDosages[0]
              || null;
            setDosage(resolvedDosage);
            resolvedDosageId = resolvedDosage?.id ?? null;
          }
        }

        // Set supply months from dosage if not explicitly in URL
        let effectiveSupplyMonths = selectedSupplyMonths || 3;
        const urlSM = searchParams.get("supplyMonths");
        if (urlSM) {
          effectiveSupplyMonths = parseInt(urlSM, 10) || 3;
        } else {
          // Check sessionStorage first (from offer page)
          const storedSM = sessionStorage.getItem("selectedSupplyMonths");
          if (storedSM) {
            effectiveSupplyMonths = parseInt(storedSM, 10) || 3;
            sessionStorage.removeItem("selectedSupplyMonths");
          } else {
            // The dosage checkout will actually price — same resolution order used above.
            const dosages = loadedProductRes?.product?.dosages || [];
            const activeDosages = dosages.filter(d => d.isActive !== false);
            const currentDosage = dosages.find(d => d.id === (dosageId || storedDosage?.dosageId))
              || activeDosages.find(d => d.isDefault)
              || activeDosages[0]
              || null;

            // Open on the term the product advertises — its starting-at basis — so the
            // "Starting at $X/mo" the member clicked on and this order summary describe
            // the same supply. Then the per-dosage isDefault, then 3 months.
            const meta = currentDosage?.supplyPricingMeta || {};
            const defaultEntry = Object.entries(meta).find(([, v]) => v?.isDefault);
            effectiveSupplyMonths = resolveBasisSupplyMonths(loadedProductRes?.product, currentDosage)
              ?? (defaultEntry ? parseInt(defaultEntry[0], 10) : 3);
          }
        }
        setSelectedSupplyMonths(effectiveSupplyMonths);
        setQuantity(effectiveSupplyMonths);

        // Calculate initial pricing
        if (productId) {
          const checkoutRes = await api.post('/checkout', {
            productId,
            dosageId: resolvedDosageId,
            quantity: effectiveSupplyMonths,
            billingAddress: {
              firstName: 'Temp',
              lastName: 'User',
              street: '123 Main St',
              city: 'Austin',
              state: 'TX',
              zipCode: '78701'
            },
            promoCode: promoCode.trim() || undefined,
            creditsToApply: applyCredit ? (pricing?.creditBalanceCents ?? 0) : 0,
            ...(higherDosagePayload ? { higherDosage: higherDosagePayload } : {})
          });

          if (checkoutRes.checkout) {
            // Merge: preserve all original product fields (systemPromo, dosages, etc.), overlay checkout info
            setProduct(prev => {
              const checkoutProduct = checkoutRes.checkout.product;
              if (prev) {
                const dosages = checkoutProduct.dosages?.length > 0 ? checkoutProduct.dosages : prev.dosages;
                return { ...prev, ...checkoutProduct, dosages };
              }
              return checkoutProduct;
            });
            setDosage(checkoutRes.checkout.dosage);
            setPricing(checkoutRes.checkout.pricing);
            if (checkoutRes.checkout.pricingBreakdown) setPricingBreakdown(checkoutRes.checkout.pricingBreakdown);
            setPricingError(null);
            initialLoadDone.current = true;
          }
        }
      } catch (error) {
        console.error('Failed to load checkout data:', error);
        toast.error('Failed to load product information');
        // A toast is transient and gates nothing. `product` is already set by the earlier
        // GET /products/:slug, so the page renders in full from here — state it as a blocker
        // too, the same way the promo catch below does, so the reason survives the toast.
        setPricingError('Pricing could not be loaded. Please refresh and try again.');
      } finally {
        setLoadingProduct(false);
      }
    };

    loadCheckoutData();
  }, [slug, searchParams.toString()]);

  // Recalculate pricing when quantity or dosage changes (NOT on initial load)
  useEffect(() => {
    if (recalcSkipRef.current) {
      if (product?.id) recalcSkipRef.current = false;
      return;
    }

    const recalculatePricing = async () => {
      if (!product?.id) return;
      if (promoValidation.status === 'validating') {
        console.log('[recalcPricing] Skipping - promo validation in progress');
        return;
      }

      try {
        const validPromo = promoValidation.status === 'valid'
          ? (promoValidation.data?.code || promoCode.trim())
          : (validatedPromoRef.current || undefined);
        const checkoutRes = await api.post('/checkout', {
          productId: product.id,
          dosageId: dosage?.id || null,
          quantity: selectedSupplyMonths || quantity,
          billingAddress: (billingData.street && billingData.city && billingData.zipCode) ? billingData : {
            firstName: 'Temp',
            lastName: 'User',
            street: '123 Main St',
            city: 'Austin',
            state: 'TX',
            zipCode: '78701'
          },
          promoCode: validPromo,
          creditsToApply: applyCredit ? (pricing?.creditBalanceCents ?? 0) : 0,
          ...(higherDosagePayload ? { higherDosage: higherDosagePayload } : {})
        });

        if (checkoutRes.checkout?.pricing) {
          setPricing(checkoutRes.checkout.pricing);
          if (checkoutRes.checkout.pricingBreakdown) setPricingBreakdown(checkoutRes.checkout.pricingBreakdown);
          setPricingError(null);
        }
      } catch (error) {
        console.error('Failed to recalculate pricing:', error);
        if (error?.code === 'PRICING_MISMATCH') {
          setPricingError('Pricing has changed. Please review your order summary.');
        } else {
          // Any other failure (network, 5xx, timeout) means setPricing never ran, so the total
          // on screen is STALE — it still describes the previous quantity/dosage/credit. Same
          // reasoning the promo catch above states: this effect is keyed on those very deps, so
          // nothing re-fires to repair it. Surface it instead of leaving a confident wrong price.
          setPricingError('Pricing could not be updated — please refresh before ordering.');
        }
      }
    };

    if (product?.id && quantity > 0) {
      recalculatePricing();
    }
  }, [quantity, product?.id, dosage?.id, applyCredit]);

  const handleBillingChange = (e) => {
    const { name, value } = e.target;
    setBillingData((prev) => ({ ...prev, [name]: value }));
  };

  const handleShippingChange = (e) => {
    const { name, value } = e.target;
    setShippingData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDosageSelect = (selectedDosage) => {
    setDosage(selectedDosage);
  };

  const handleCardChange = (e) => {
    const { name, value } = e.target;

    // Format card number with spaces
    if (name === 'number') {
      const cleaned = value.replace(/\D/g, '').slice(0, 16);
      const formatted = cleaned.replace(/(\d{4})(?=\d)/g, '$1 ');
      setCardData((prev) => ({ ...prev, [name]: formatted }));
      return;
    }

    // Format expiration as MM/YY
    if (name === 'expiration') {
      const cleaned = value.replace(/\D/g, '').slice(0, 4);
      if (cleaned.length >= 2) {
        const formatted = `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
        setCardData((prev) => ({ ...prev, [name]: formatted }));
      } else {
        setCardData((prev) => ({ ...prev, [name]: cleaned }));
      }
      return;
    }

    // CVV max 4 digits
    if (name === 'cvv') {
      const cleaned = value.replace(/\D/g, '').slice(0, 4);
      setCardData((prev) => ({ ...prev, [name]: cleaned }));
      return;
    }

    setCardData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAchChange = (e) => {
    const { name, value } = e.target;

    // Routing number: 9 digits only
    if (name === "routingNumber") {
      const cleaned = value.replace(/\D/g, "").slice(0, 9);
      setAchData((prev) => ({ ...prev, [name]: cleaned }));
      return;
    }

    // Account number: digits only, reasonable max length
    if (name === "accountNumber") {
      const cleaned = value.replace(/\D/g, "").slice(0, 17);
      setAchData((prev) => ({ ...prev, [name]: cleaned }));
      return;
    }

    setAchData((prev) => ({ ...prev, [name]: value }));
  };

  const validateStep1 = () => {
    const required = ['firstName', 'lastName', 'street', 'city', 'state', 'zipCode'];
    for (const field of required) {
      if (!billingData[field]?.trim()) {
        toast.error(`Please enter ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
        return false;
      }
    }
    // Validate ZIP code format
    if (!/^\d{5}(-\d{4})?$/.test(billingData.zipCode)) {
      toast.error('Please enter a valid ZIP code');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    // Card #54415. The one case where NEITHER arm may be used. ⛔ Checked before the arm branch,
    // because on this state `isStripeArm` is (correctly) false and the PAN checks below would
    // then refuse with "enter a valid card number" — advice the member cannot act on, since the
    // raw-card surface is not the surface this site is configured to take.
    if (cardEntryError) {
      toast.error(cardEntryError.message);
      return false;
    }

    // Card #53493. On the Stripe arm this origin never sees a PAN, so there is nothing here to
    // length-check — the provider's iframe validated the card and minted a reference. The only
    // thing checkout can assert is that the reference EXISTS.
    // ⛔ Do not also run the PAN checks below on this arm: `cardData` is empty by design there,
    // so they would refuse every valid hosted-field payment.
    if (useHostedCardForm) {
      if (!paymentMethodId) {
        toast.error('Please enter and confirm your card details');
        return false;
      }
      return true;
    }

    // Validate card number (16 digits)
    const cardNumberClean = cardData.number.replace(/\s/g, '');
    if (cardNumberClean.length < 13 || cardNumberClean.length > 16) {
      toast.error('Please enter a valid card number');
      return false;
    }

    // Validate expiration (MM/YY)
    if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(cardData.expiration)) {
      toast.error('Please enter expiration as MM/YY');
      return false;
    }

    // Validate CVV (3-4 digits)
    if (!/^\d{3,4}$/.test(cardData.cvv)) {
      toast.error('Please enter a valid CVV');
      return false;
    }

    return true;
  };

  // Validate ACH payment fields
  const validateAchPayment = () => {
    if (!achData.bankName?.trim()) {
      toast.error('Please enter bank name');
      return false;
    }
    
    // Routing number must be exactly 9 digits
    if (!/^\d{9}$/.test(achData.routingNumber)) {
      toast.error('Please enter a valid 9-digit routing number');
      return false;
    }
    
    // Account number must be 4-17 digits
    if (!/^\d{4,17}$/.test(achData.accountNumber)) {
      toast.error('Please enter a valid account number (4-17 digits)');
      return false;
    }
    
    // Account type must be checking or savings
    if (!['checking', 'savings'].includes(achData.accountType)) {
      toast.error('Please select account type');
      return false;
    }
    
    return true;
  };

  const handleContinueToPayment = () => {
    if (validateStep1()) {
      setStep(2);
    }
  };

  const handleContinueToReview = () => {
    if (validateStep2()) {
      setStep(3);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep1()) return;
    
    // Validate payment method specific fields
    if (paymentMethod === 'card') {
      if (!validateStep2()) return;
    } else if (paymentMethod === 'ach') {
      // Card #54400: on the hosted arm the bank was linked and verified in the provider's own
      // Element, so the only thing to assert is that the reference EXISTS — as for a card.
      if (isStripeArm && !achPaymentMethodId) {
        toast.error('Please link and confirm your bank account');
        return;
      }
      if (!isStripeArm && !validateAchPayment()) return;
    }

    if (!user) {
      toast.error('Please log in to complete checkout');
      navigate('/login', { state: { from: window.location.pathname + window.location.search } });
      return;
    }

    // Prevent duplicate submissions (ref is synchronous, blocks before React re-renders)
    if (submittingRef.current) return;
    submittingRef.current = true;

    // Card 54409. A retry of this submission reuses its id; the first attempt mints one.
    const submissionId = submissionIdRef.current ?? (submissionIdRef.current = createSubmissionId());

    setLoading(true);
    setPaymentError(null);

    try {
      // Submit checkout with payment hold
      const validPromo = promoValidation.status === 'valid' ? promoCode.trim() : undefined;
      const response = await api.post('/checkout/submit', {
        termsVersion: TERMS_VERSION,
        legalConsentAgreed: legalConsentAgreed,
        productId: product.id,
        providerType: product?.providerType || "DrCare",
        superOfferId: superOfferId || null,
        dosageId: dosage?.id || null,
        quantity: selectedSupplyMonths || quantity,
        // ROOT CAUSE (2026-10-21, higher-dosage refill 409): this used to prefer
        // pb.totalDueNow, which is the QUOTE BREAKDOWN total and EXCLUDES the increased
        // strength fee (the server holds that fee outside pricingEngine). The submit path
        // compares against engine.totalDueNow + higherDosageFeeCents, so every higher-dosage
        // refill sent 3000 cents short and locked Place Order forever. Send the total the
        // member was SHOWN (pricing.total, which carries the fee); cents, exact.
        expectedTotal: pricing?.total ?? pb?.totalDueNow ?? null,
        submissionId,
        billingAddress: billingData,
        shippingAddress: sameAsShipping ? null : shippingData,
        sameAsShipping,
        promoCode: validPromo,
        creditsToApply: applyCredit ? (pricing?.creditBalanceCents ?? 0) : 0,
        ...(higherDosagePayload ? { higherDosage: higherDosagePayload } : {}),
        paymentMethod,
        payWithFsaCard: payWithFsaCard === true,
        ...(appointmentTime?.utcTime ? { preferredAppointmentTime: appointmentTime.utcTime, appointmentTimezone: appointmentTime.timezone || null, appointmentTimeFormatted: appointmentTime.time || null } : {}),
        // Card #53493. The two arms send DIFFERENT things and ⛔ must never send both:
        //   Stripe    — a top-level `paymentMethodId` and NO `card` key at all. The server
        //               refuses a PAN here 400 `RAW_CARD_NOT_ACCEPTED`, and requires the
        //               reference 400 `PAYMENT_METHOD_REQUIRED`.
        //   Incumbent — the raw `card` object it has always taken, byte-identical to before,
        //               and no `paymentMethodId` (#53299 is explicit that this path stays).
        ...(paymentMethod === 'card' ? buildCardPaymentPayload({
          isStripeArm: useHostedCardForm,
          paymentMethodId,
          cardData
        }) : {
          // Card #54400: the hosted arm sends the linked bank method and ⛔ no bank numbers.
          ...(isStripeArm ? { paymentMethodId: achPaymentMethodId } : {
            achData: {
              bankName: achData.bankName,
              routingNumber: achData.routingNumber,
              accountNumber: achData.accountNumber,
              accountType: achData.accountType
            }
          }),
          achPromoApplied
        }),
        // Subscription Clause acceptance — required for every supply term (60 Day is a real
        // 2-month commitment); the payload only ever comes from the completed signing modal
        ...(subscriptionClauseAgreed && subscriptionConsentData ? {
          subscriptionConsent: {
            checkboxes: subscriptionConsentData.checkboxes,
            agreementResponse: subscriptionConsentData.agreementResponse,
            signatureMode: subscriptionConsentData.signatureMode,
            signatureData: subscriptionConsentData.signatureData,
            adoptedName: subscriptionConsentData.adoptedName,
            supplyMonths: selectedSupplyMonths,
            monthlyPrice: pricing?.membership?.monthlyRateCents != null
              ? Math.round(pricing.membership.monthlyRateCents / 100)
              : (membershipTier?.monthlyPrice || 0)
          }
        } : {})
      }, { timeoutMs: CHECKOUT_SUBMIT_TIMEOUT_MS });

      if (response.success) {
        // Clear card data from memory immediately
        setCardData({ number: '', expiration: '', cvv: '', nameOnCard: '' });
        setPaymentMethodId(null);  // card #53493: a minted reference is single-use
        setAchPaymentMethodId(null);  // card #54400: a declined bank is re-linked, never retried
        setAchData({ bankName: '', routingNumber: '', accountNumber: '', accountType: 'checking' });

        // Clear sessionStorage
        sessionStorage.removeItem('selectedDosage');
        sessionStorage.removeItem('oval_higher_dosage_request');

        toast.success('Order placed successfully!');
        navigate('/checkout/success', {
          state: { order: response.order },
          replace: true
        });
      }
    } catch (error) {
      // `api` is a fetch wrapper (web/services/api.js): handleResponse puts the parsed
      // body on `error.data` and the code/status on `error.code`/`error.status`. There is
      // no axios `.response` envelope anywhere in web/, so reading one made every branch
      // below unreachable and collapsed each structured failure into the generic else.
      const errorData = error.data;
      const errorMessage = errorData?.message || errorData?.error || error.message || 'Failed to place order';

      // Card 54409. A definitive server answer ends this submission — the next attempt is a new
      // order and must not reuse a provider key whose result the provider has already cached.
      // A 409 means the FIRST attempt is still in flight and a 503 is infrastructure; both keep
      // the id so the retry collapses instead of racing.
      if (error.status && error.status !== 409 && error.status !== 503) {
        submissionIdRef.current = null;
      }

      if (errorData?.code === REQUEST_TIMEOUT_CODE || error.code === REQUEST_TIMEOUT_CODE) {
        // Card #54415 (D3). The request was ABORTED by our own budget, so nothing is known about
        // the outcome — it is neither a decline nor a transport error, and it MUST be retryable:
        // `submittingRef` is released in `finally` and the modal's Retry re-invokes handleSubmit.
        // ⛔ Checked BEFORE the `includes('payment')` branch below, which would otherwise swallow
        // it and tell the member their card was declined.
        setPaymentError({ code: REQUEST_TIMEOUT_CODE, message: REQUEST_TIMEOUT_MESSAGE });
        setShowDeclineModal(true);
      } else if (errorData?.code === 'PRICING_MISMATCH') {
        setPricingError('Pricing has changed. Please review your order summary.');
        toast.error('Pricing has changed — please review your order before submitting.');
      } else if (errorData?.code === 'PAYMENT_FAILED' || errorMessage.toLowerCase().includes('payment')) {
        setPaymentError({
          code: errorData?.code || 'PAYMENT_FAILED',
          message: errorMessage
        });
        setShowDeclineModal(true);
      } else if (errorData?.code === 'DUPLICATE_ORDER') {
        toast.error('Your checkout has already been submitted. Please allow a few minutes for it to process.');
      } else if (error.status === 401) {
        toast.error('Please log in to complete checkout');
        navigate('/login', { state: { from: window.location.pathname + window.location.search } });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  const handleRetryPayment = async () => {
    setShowDeclineModal(false);
    await handleSubmit();
  };

  const handleTryDifferentCard = () => {
    setShowDeclineModal(false);
    setCardData({ number: '', expiration: '', cvv: '', nameOnCard: '' });
        setPaymentMethodId(null);  // card #53493: a minted reference is single-use
        setAchPaymentMethodId(null);  // card #54400: a declined bank is re-linked, never retried
        setAchData({ bankName: '', routingNumber: '', accountNumber: '', accountType: 'checking' });
    setStep(2);
  };

  const handleContactSupport = () => {
    setShowDeclineModal(false);
    window.location.href = 'mailto:support@oval.health';
  };

  // ACH promo modal handlers
  const handleAchPromoAccept = () => {
    setShowAchPromoModal(false);
    setPaymentMethod('ach');
    setAchPromoApplied(true);
    // Scroll to ACH fields
    setTimeout(() => {
      const achSection = document.querySelector('[data-ach-fields]');
      if (achSection) {
        achSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleAchPromoDecline = () => {
    setShowAchPromoModal(false);
    // User chose to stay with card, don't show modal again this session
  };

  // Signature captured inside the modal — kept so reopening the modal restores it
  const handleModalSignatureCapture = (sigData) => {
    setSharedSignatureData(sigData);
  };

  const getCurrentSignatureData = () => sharedSignatureData;

  // Money lines (receipt): always exact to the cent. The displayed total has to equal the API's
  // pricing.total and the amount the gateway actually charges. Rounding each line up to a whole
  // dollar broke that twice over: Math.ceil does not distribute over a sum, so a summary with two
  // or more fractional lines could visibly fail to add up, and the total itself could exceed the
  // charge (e.g. micc 60 Day: displayed $50, charged $49.99).
  const formatPrice = (cents) => {
    if (!cents && cents !== 0) return '$0.00';
    return `$${(cents / 100).toFixed(2)}`;
  };

  // Marketing rates (per-month price, supply cards, retail strike, savings panel): whole-dollar
  // prices stay clean ("$51/mo"), but a fractional one still renders exactly ("$49.99/mo").
  // Never rounds — a rate must not contradict the money line it is derived from.
  const formatRate = (cents) => {
    if (!cents && cents !== 0) return '$0';
    return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
  };

  // Extract price from tier-aware or flat supply pricing data
  const extractTierPrice = (priceData) => {
    if (!priceData) return 0;
    if (typeof priceData === 'object') {
      const tier = pricing?.tierType || 'access';
      return priceData[tier] ?? priceData.access ?? 0;
    }
    return priceData;
  };

  // Backend pricing breakdown (source of truth for order summary)
  const pb = pricingBreakdown;

  // WHICH server-supplied supply map is charge-exact for THIS member.
  // `supplyPricingEffective` has each tier's `product_price_modifier` already baked into the
  // stored rate; `supplyPricing` is the raw rate. Both are keyed by tier, and `pricing.tierType`
  // collapses "Oval Access member" (modifier +0.10) and "no membership at all" (no modifier)
  // onto the same key `access`, so the tier alone CANNOT choose between them — picking either
  // map unconditionally quotes one of those two populations a price it will not be charged.
  // The backend can tell them apart: pricingEngine sets
  // `adjustedMedicationPrice = round(base * (1 + modifier))` and nothing else (promos are
  // applied downstream), so these two fields differ if and only if THIS member carries a
  // modifier. ⛔ No modifier arithmetic in the browser — this only PICKS a server map.
  const hasTierPriceModifier = pb?.baseMedicationPrice > 0 && pb?.adjustedMedicationPrice > 0
    && pb.adjustedMedicationPrice !== pb.baseMedicationPrice;
  // ⚠️ The effective map survives only on the PRODUCT copy. `POST /api/checkout` returns a
  // slimmer `checkout.dosage` (no effective map) and :429 assigns it straight over the
  // product-sourced one, so reading it off `dosage` alone silently falls back to raw and this
  // whole fix reads as a no-op. `checkout.product.dosages` is undefined, so :423 keeps
  // `prev.dosages` — which is where the field still lives, and is why DosageSelector (fed from
  // `product.dosages`) already quotes the modifier correctly while these cards did not.
  const effectiveSupplyPricing = dosage?.supplyPricingEffective
    ?? product?.dosages?.find((d) => d.id === dosage?.id)?.supplyPricingEffective;
  const displaySupplyPricing = (hasTierPriceModifier ? effectiveSupplyPricing : dosage?.supplyPricing)
    ?? dosage?.supplyPricing;

  // Supply pricing: per-month price for a term, or the legacy flat `price` column.
  const getDisplayPrice = (months) => {
    if (!dosage) return 0;
    const key = String(months);
    if (displaySupplyPricing && displaySupplyPricing[key] !== undefined) {
      return extractTierPrice(displaySupplyPricing[key]);
    }
    return dosage.price || 0;
  };

  // Supply pricing display values — use backend pricingBreakdown as source of truth
  const hasTieredPricing = displaySupplyPricing && Object.keys(displaySupplyPricing).length > 0;
  const rawSupplyPrice = getDisplayPrice(selectedSupplyMonths);
  const oneMonthPrice = getDisplayPrice(1);
  const supplyPrice = pb?.adjustedMedicationPrice ?? rawSupplyPrice;
  const supplyTotal = pb?.subtotal ?? (rawSupplyPrice * selectedSupplyMonths);

  // The selected term expressed the way a human reads it: rate per INTERNAL month, over
  // internal months. Key "1" sells a 60 Day term (two internal months), so its stored rate
  // halves; "3"/"6"/"12" are a no-op. Display only — supplyTotal stays charge-exact.
  const selectedTerm = getSupplyTerm(String(selectedSupplyMonths));
  const supplyRate = toPerMonthRate(supplyPrice, String(selectedSupplyMonths));

  // Multi-month savings for display — both sides come from `displaySupplyPricing`, so the
  // comparison is charge-exact for a modifier-carrying member instead of quoting raw rates.
  const savingsVsOneMonth = hasTieredPricing && selectedSupplyMonths > 1 && oneMonthPrice > rawSupplyPrice
    ? (oneMonthPrice - rawSupplyPrice) * selectedSupplyMonths : 0;
  const savingsPct = oneMonthPrice > 0 && savingsVsOneMonth > 0
    ? Math.round(((oneMonthPrice - rawSupplyPrice) / oneMonthPrice) * 100) : 0;

  // Retail-vs-Oval saving over the SELECTED TERM, derived once so the headline and the detail
  // row cannot disagree. Same identity checkoutOrderSummaryRate.test.js pins:
  // (retail - rate) * internalMonths === retail * internalMonths - supplyTotal.
  // ⚠️ The guard is deliberately a SUBTRACTION, not the same comparison the three JSX sites
  // use: that comparison's source text is COUNT-pinned at exactly 3 by that suite, so a 4th
  // occurrence anywhere in this file -- including inside a comment -- reddens it.
  const hasRetailSaving = Boolean(dosage?.retailPrice) && dosage.retailPrice - supplyRate > 0;
  const retailSavings = hasRetailSaving
    ? (dosage.retailPrice - supplyRate) * selectedTerm.internalMonths : 0;
  const retailSavingsPct = hasRetailSaving
    ? Math.round(((dosage.retailPrice - supplyRate) / dosage.retailPrice) * 100) : 0;

  // System promo discount — from backend month-by-month breakdown
  const systemPromo = product?.systemPromo || null;
  const systemPromoDiscount = pb?.monthByMonthBreakdown
    ? {
        total: pb.monthByMonthBreakdown.reduce((sum, m) => sum + m.discountAmount, 0),
        breakdown: pb.monthByMonthBreakdown
          .filter(m => m.discountAmount > 0)
          .map(m => ({ month: m.month, pct: m.discountPercent, amount: m.discountAmount }))
      }
    : { total: 0, breakdown: [] };

  // ACH promo discount (one month free — handled separately from pricing engine)
  const achDiscount = (achPromoApplied && pricing?.unitPrice) ? pricing.unitPrice : 0;

  // Account credit (Task 8) — balance + applied amount come straight from backend pricing.
  // When applyCredit is on, backend re-prices so pb.totalDueNow already reflects the deduction.
  const creditBalanceCents = pricing?.creditBalanceCents ?? 0;
  const appliedCreditsCents = applyCredit ? (pricing?.appliedCreditsCents ?? 0) : 0;

  // Increased strength fee — backend-computed; pricing.total already includes it in BOTH
  // contracts: higher-dosage refill (fee outside pb.totalDueNow) and per-month normal checkout
  // (STRENGTH_FEE_PER_MONTH, billedInTotal — fee inside pb.totalDueNow too). Displayed total is
  // always the API's pricing.total when a fee exists — never client-side math.
  const increasedStrengthFeeCents = pricing?.increasedStrengthFee?.amountCents ?? 0;
  const strengthFeeMultiplier = pricing?.increasedStrengthFee?.multiplier ?? 1;
  const totalDueCents = increasedStrengthFeeCents > 0
    ? (pricing?.total ?? 0)
    : (pb?.totalDueNow ?? pricing?.total ?? 0);

  // Hold and waiver display helpers
  const isConsultationWaived = pricing?.waivedItems?.includes('consultation_fee');
  const isShippingWaived = pricing?.waivedItems?.includes('shipping_cost');
  const isTierHold = pricing?.tierSavings?.consultationFeeWaived > 0 && !isConsultationWaived;
  const heldConsultationFee = pricing?.tierSavings?.consultationFeeWaived || 0;

  // May this checkout still show a scheduling affordance? One shared predicate, so checkout cannot
  // drift from the other DrCare surfaces. It fails CLOSED to the asynchronous contract: anything it
  // cannot read as an explicit `requires_appointment: true` gets the async-review block instead.
  //
  // The `order` argument is genuinely null HERE and that is not an oversight: checkout runs before
  // an order exists — this page's own submit creates it — and the refill scheduling path never
  // reaches /checkout at all. A refill slot returns to /orders (`CalendarDemo.jsx:207`) and is paid
  // inside RefillCheckoutModal, which asks `refill.schedulingRequired` for itself. So only the
  // product arm of the predicate can fire on this page.
  const canOfferScheduling = shouldOfferScheduling(product, null);

  // Card 54405: a Doctor Now clinician picks the dose, so the summary shows the notice instead of a
  // dosage amount or selector. Presentation only — `dosage` is still resolved and still sent as
  // `dosageId` in every /checkout and /checkout/submit body, so pricing is unchanged.
  const isDosageSelectable = canCustomerSelectDosage(product);

  // Why Place Order is disabled — rendered immediately above BOTH buttons (card and ACH), which
  // carry the identical `disabled` expression. Built once here so the two arms cannot drift apart.
  const placeOrderBlockers = derivePlaceOrderBlockers({
    legalConsentAgreed,
    pricing,
    pricingError,
    subscriptionClauseAgreed,
    subscriptionConsentData,
  });

  const placeOrderBlockerNotice = placeOrderBlockers.length > 0 ? (
    <div className={styles.placeOrderBlockers} role="status" data-place-order-blockers>
      <p className={styles.placeOrderBlockersTitle}>To place your order, please complete</p>
      <ul className={styles.placeOrderBlockersList}>
        {placeOrderBlockers.map(({ key, label, actionLabel }) => (
          <li key={key} data-blocker={key}>
            {label}
            {actionLabel ? (
              <>
                {' — '}
                <button
                  type="button"
                  className={styles.viewTermsBtn}
                  onClick={() => setIsSubscriptionModalOpen(true)}
                >
                  {actionLabel}
                </button>
              </>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  ) : null;

  // Card #54415 (D3). The NAMED error state that replaces the card surface when the switch and
  // the key disagree. Quotable by attribute (`data-card-entry-error`) so the proof does not rest
  // on prose, and it carries its own retry — the state is recoverable, never a dead end.
  const cardEntryErrorNotice = cardEntryError ? (
    <div className={styles.pricingErrorBanner} role="alert" data-card-entry-error={cardEntryError.code}>
      <p>{cardEntryError.message}</p>
      <button type="button" className={styles.viewTermsBtn} onClick={reloadPaymentConfig}>
        Try again
      </button>
    </div>
  ) : null;

  if (loadingProduct) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <p>Loading checkout...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className={styles.page}>
        <div className={styles.error}>
          <h2>Product Not Found</h2>
          <p>Unable to load product information for checkout.</p>
          <Button onClick={() => navigate('/catalog')}>Back to Catalog</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.standaloneHeader}>
        <div className={styles.logoGroup}><button onClick={() => navigate(-1)} className={styles.backBtn} title="Go back"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><polyline points="12 19 5 12 12 5"/></svg></button><img loading="lazy" src="/oval-logo.webp" alt="OVAL" className={styles.logoImage} onClick={() => navigate("/dashboard")} style={{cursor: "pointer"}} /></div>
        <span className={styles.secureText}>Secure Checkout</span>
      </div>

      <div className={styles.layout}>
        <div className={styles.main}>
          {/* Billing Form */}
          <Card className={styles.formCard}>
            <h2>Shipping Address</h2>
              <div className={styles.form}>
                <div className={styles.row}>
                  <Input
                    label="First Name"
                    name="firstName"
                    value={shippingData.firstName}
                    onChange={handleShippingChange}
                    required
                  />
                  <Input
                    label="Last Name"
                    name="lastName"
                    value={shippingData.lastName}
                    onChange={handleShippingChange}
                    required
                  />
                </div>
                <Input
                  label="Street Address"
                  name="street"
                  value={shippingData.street}
                  onChange={handleShippingChange}
                  required
                />
                <Input
                  label="Street Address 2 (Optional)"
                  name="street2"
                  value={shippingData.street2}
                  onChange={handleShippingChange}
                />
                <div className={styles.row}>
                  <Input
                    label="City"
                    name="city"
                    value={shippingData.city}
                    onChange={handleShippingChange}
                    required
                  />
                  <Input
                    label="State"
                    name="state"
                    value={shippingData.state}
                    onChange={handleShippingChange}
                    required
                  />
                  <Input
                    label="ZIP Code"
                    name="zipCode"
                    value={shippingData.zipCode}
                    onChange={handleShippingChange}
                    placeholder="12345"
                    required
                  />
                </div>
                <Input
                  label="Phone Number (Optional)"
                  name="phone"
                  value={shippingData.phone}
                  onChange={handleShippingChange}
                  placeholder="+1 (555) 123-4567"
                />

                <div className={styles.checkbox}>
                  <input
                    type="checkbox"
                    id="sameAsShipping"
                    checked={sameAsShipping}
                    onChange={(e) => setSameAsShipping(e.target.checked)}
                  />
                  <label htmlFor="sameAsShipping">Billing address same as shipping address</label>
                </div>

                {/* Subscription Clause — every supply term is a real multi-month commitment
                    (60 Day = 2 months), so completing the signing modal is always required */}
                <div className={styles.subscriptionClauseSection}>
                  <div className={styles.subscriptionClauseHeader}>
                    <h3 className={styles.sectionTitle}>Subscription Clause</h3>
                    {subscriptionClauseAgreed && subscriptionConsentData && (
                      <button
                        type="button"
                        className={styles.viewTermsBtn}
                        onClick={() => setIsSubscriptionModalOpen(true)}
                      >
                        Review Terms
                      </button>
                    )}
                  </div>
                  <p className={styles.signatureInstruction}>
                    Your membership unlocks discounted member pricing, and a {getSupplyLabel(selectedSupplyMonths)} supply
                    front-loads those discounted months — so membership is owed for the full term, and
                    cancelling early charges the remaining months in full. Review and sign the
                    Subscription Terms &amp; Conditions to place your order.
                  </p>
                  {subscriptionClauseAgreed && subscriptionConsentData ? (
                    <div className={styles.checkbox}>
                      <input
                        type="checkbox"
                        id="subscriptionClauseSigned"
                        checked
                        readOnly
                        disabled
                      />
                      <label htmlFor="subscriptionClauseSigned">
                        Terms signed{subscriptionConsentData.adoptedName ? ` by ${subscriptionConsentData.adoptedName}` : ''} — all acknowledgments and signature captured
                      </label>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className={styles.viewTermsBtn}
                      onClick={() => setIsSubscriptionModalOpen(true)}
                    >
                      Review &amp; Sign Terms
                    </button>
                  )}
                </div>

                {!sameAsShipping && (
                  <>
                    <h3 className={styles.sectionTitle}>Billing Address</h3>
                    <div className={styles.row}>
                      <Input
                        label="First Name"
                        name="firstName"
                        value={billingData.firstName}
                        onChange={handleBillingChange}
                        required
                      />
                      <Input
                        label="Last Name"
                        name="lastName"
                        value={billingData.lastName}
                        onChange={handleBillingChange}
                        required
                      />
                    </div>
                    <Input
                      label="Street Address"
                      name="street"
                      value={billingData.street}
                      onChange={handleBillingChange}
                      required
                    />
                    <Input
                      label="Street Address 2 (Optional)"
                      name="street2"
                      value={billingData.street2}
                      onChange={handleBillingChange}
                    />
                    <div className={styles.row}>
                      <Input
                        label="City"
                        name="city"
                        value={billingData.city}
                        onChange={handleBillingChange}
                        required
                      />
                      <Input
                        label="State"
                        name="state"
                        value={billingData.state}
                        onChange={handleBillingChange}
                        required
                      />
                      <Input
                        label="ZIP Code"
                        name="zipCode"
                        value={billingData.zipCode}
                        onChange={handleBillingChange}
                        placeholder="12345"
                        required
                      />
                    </div>
                    <Input
                      label="Phone Number"
                      name="phone"
                      value={billingData.phone}
                      onChange={handleBillingChange}
                      placeholder="+1 (555) 123-4567"
                      required
                    />
                  </>
                )}

              </div>
            </Card>

          {/* Payment Form */}
          <Card className={styles.formCard}>
            <h2>Payment Details</h2>
            {/* Payment Method Tabs */}
            <div className={styles.paymentMethodTabs}>
              <button
                type="button"
                className={`${styles.paymentTab} ${paymentMethod === "card" ? styles.paymentTabActive : ""}`}
                onClick={() => setPaymentMethod("card")}
              >
                Credit Card
              </button>
              {/* Card #54400 (D6): live on the hosted arm, where the bank is linked in the provider's
                  Element and debited through stripe/ach.js; still "Coming Soon" on the incumbent. */}
              {isStripeArm ? (
                <button
                  type="button"
                  className={`${styles.paymentTab} ${paymentMethod === "ach" ? styles.paymentTabActive : ""}`}
                  onClick={() => setPaymentMethod("ach")}
                >
                  ACH Bank Transfer
                </button>
              ) : (
                <button
                  type="button"
                  className={`${styles.paymentTab} ${paymentMethod === "ach" ? styles.paymentTabActive : ""}`}
                  onClick={() => {}} disabled style={{opacity: 0.5, cursor: "not-allowed", position: "relative"}}
                >
                  ACH Bank Transfer &mdash; Coming Soon
                </button>
              )}
            </div>
            {paymentMethod === 'card' && (
              <>
                <p className={styles.note}>
                  Your card will be authorized for the total amount. The charge will be processed after your order is approved.
                </p>
                <div className={styles.legalConsentCheckbox}>
                  <input
                    type="checkbox"
                    id="payWithFsaCard"
                    checked={payWithFsaCard}
                    onChange={(e) => {
                      setPayWithFsaCard(e.target.checked);
                      // A hosted-card reference belongs to the other merchant; drop it when the
                      // member switches merchants so a stale reference can never be submitted.
                      setPaymentMethodId(null);
                    }}
                  />
                  <label htmlFor="payWithFsaCard">
                    Paying with an HSA/FSA card — send this order to our alternate processor
                  </label>
                </div>
                <div className={styles.form}>
{cardEntryError ? cardEntryErrorNotice : useHostedCardForm ? (
                    /* Card #53493. The provider's own iframe owns the PAN, expiry and CVC.
                       ⛔ DO NOT re-wrap these in Oval <Input>s and ⛔ do not read the number
                       back out even to length-check it — that puts it in this document and
                       undoes the entire point. The component mints a reference and hands
                       back nothing else. */
                    <Suspense fallback={<p className={styles.note}>Preparing secure card entry...</p>}>
                      <StripePaymentElement
                        publishableKey={paymentConfig.publishableKey}
                        onPaymentMethod={setPaymentMethodId}
                        submitLabel={paymentMethodId ? 'Card ready' : 'Use this card'}
                      />
                      {paymentMethodId && (
                        <p className={styles.note} role="status">
                          Card confirmed with the payment provider. Place your order below.
                        </p>
                      )}
                    </Suspense>
                  ) : (
                    <>
                    <Input
                      label="Card Number" onFocus={handleCardNumberFocus}
                      name="number"
                      value={cardData.number}
                      onChange={handleCardChange}
                      placeholder="4111 1111 1111 1111"
                      autoComplete="cc-number"
                      required
                    />
                    <div className={styles.row}>
                      <Input
                        label="Expiry Date"
                        name="expiration"
                        value={cardData.expiration}
                        onChange={handleCardChange}
                        placeholder="MM/YY"
                        autoComplete="cc-exp"
                        required
                      />
                      <Input
                        label="CVV"
                        name="cvv"
                        type="password"
                        value={cardData.cvv}
                        onChange={handleCardChange}
                        placeholder="123"
                        autoComplete="cc-csc"
                        required
                      />
                    </div>
                    <Input
                      label="Name on Card"
                      name="nameOnCard"
                      value={cardData.nameOnCard}
                      onChange={handleCardChange}
                      placeholder="John Doe"
                      autoComplete="cc-name"
                    />
                    </>
                  )}
                  
                  {/* Legal Consent Section */}
                  <div className={styles.legalConsentCard}>
                    <h4 className={styles.legalConsentTitle}>Important Disclosures</h4>
                    <ul className={styles.legalConsentList}>
                      <li>Oval Care is not a healthcare provider — it is a technology platform</li>
                      <li>Your healthcare provider is solely responsible for your medical care</li>
                      <li>Prescriptions are not guaranteed and depend on provider judgment</li>
                      <li>You assume all risks of medications, including side effects</li>
                      <li>You release Oval Care from claims related to medical care</li>
                    </ul>
                    <div className={styles.legalConsentCheckbox}>
                      <input
                        type="checkbox"
                        id="legalConsentCard"
                        checked={legalConsentAgreed}
                        onChange={(e) => setLegalConsentAgreed(e.target.checked)}
                      />
                      <label htmlFor="legalConsentCard">
                        I acknowledge and agree to all of the above, and I have read and agree to the{' '}
                        <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>{' '}
                        and <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                      </label>
                    </div>
                  </div>
                  {placeOrderBlockerNotice}
                  <Button onClick={handleSubmit} loading={loading} disabled={placeOrderBlockers.length > 0} fullWidth>
                    Place Order
                  </Button>
                </div>
              </>
            )}
            {paymentMethod === "ach" && (
              <div data-ach-fields>
                <p className={styles.note}>
                  ACH payments are processed directly from your bank account. Your first month is free when using ACH!
                </p>
                <div className={styles.form}>
{isStripeArm ? (
                  /* Card #54400 (D6). The provider's Element links and instantly verifies the bank
                     and shows its own mandate; only the minted method id comes back. ⛔ No routing
                     or account number ever exists in this document on this arm. */
                  <Suspense fallback={<p className={styles.note}>Preparing secure bank linking...</p>}>
                    <StripePaymentElement
                      publishableKey={paymentConfig.publishableKey}
                      methodType="us_bank_account"
                      onPaymentMethod={setAchPaymentMethodId}
                      submitLabel={achPaymentMethodId ? 'Bank account ready' : 'Use this bank account'}
                    />
                    {achPaymentMethodId && (
                      <p className={styles.note} role="status">
                        Bank account linked with the payment provider. Place your order below.
                      </p>
                    )}
                  </Suspense>
                ) : (
                  <>
                  <Input
                    label="Bank Name"
                    name="bankName"
                    value={achData.bankName}
                    onChange={handleAchChange}
                    placeholder="e.g., Chase Bank"
                    required
                  />
                  <Input
                    label="Routing Number"
                    name="routingNumber"
                    value={achData.routingNumber}
                    onChange={handleAchChange}
                    placeholder="9 digits"
                    maxLength={9}
                    required
                  />
                  <Input
                    label="Account Number"
                    name="accountNumber"
                    type="password"
                    value={achData.accountNumber}
                    onChange={handleAchChange}
                    placeholder="Account number"
                    required
                  />
                  <div className={styles.achAccountLastFour}>
                    {achData.accountNumber.length > 0 && (
                      <span>Last 4: ••••{achData.accountNumber.slice(-4)}</span>
                    )}
                  </div>
                  <div className={styles.achAccountType}>
                    <label className={styles.achLabel}>Account Type</label>
                    <div className={styles.achRadioGroup}>
                      <label className={styles.achRadio}>
                        <input
                          type="radio"
                          name="accountType"
                          value="checking"
                          checked={achData.accountType === "checking"}
                          onChange={handleAchChange}
                        />
                        <span>Checking</span>
                      </label>
                      <label className={styles.achRadio}>
                        <input
                          type="radio"
                          name="accountType"
                          value="savings"
                          checked={achData.accountType === "savings"}
                          onChange={handleAchChange}
                        />
                        <span>Savings</span>
                      </label>
                    </div>
                  </div>
                  </>
                )}
                  
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', margin: '16px 0' }}>
                    <input
                      type="checkbox"
                      id="telehealthConsentAch"
                      checked={telehealthConsentAgreed}
                      onChange={(e) => setTelehealthConsentAgreed(e.target.checked)}
                      style={{ marginTop: '4px', accentColor: '#E8732A' }}
                    />
                    <label htmlFor="telehealthConsentAch" style={{ fontSize: '13px', color: '#555', lineHeight: '1.4' }}>
                      I agree to the <a href="/telehealth-consent" target="_blank" rel="noopener noreferrer" style={{ color: '#E8732A', textDecoration: 'underline' }}>Telehealth Consent &amp; Privacy Policy</a>, including telemedicine services, compounded medication risks, and electronic signature authorization.
                    </label>
                  </div>

                  {/* Legal Consent Section */}
                  <div className={styles.legalConsentCard}>
                    <h4 className={styles.legalConsentTitle}>Important Disclosures</h4>
                    <ul className={styles.legalConsentList}>
                      <li>Oval Care is not a healthcare provider — it is a technology platform</li>
                      <li>Your healthcare provider is solely responsible for your medical care</li>
                      <li>Prescriptions are not guaranteed and depend on provider judgment</li>
                      <li>You assume all risks of medications, including side effects</li>
                      <li>You release Oval Care from claims related to medical care</li>
                    </ul>
                    <div className={styles.legalConsentCheckbox}>
                      <input
                        type="checkbox"
                        id="legalConsentAch"
                        checked={legalConsentAgreed}
                        onChange={(e) => setLegalConsentAgreed(e.target.checked)}
                      />
                      <label htmlFor="legalConsentAch">
                        I acknowledge and agree to all of the above, and I have read and agree to the{' '}
                        <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>{' '}
                        and <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                      </label>
                    </div>
                  </div>
                  {placeOrderBlockerNotice}
                  <Button onClick={handleSubmit} loading={loading} disabled={placeOrderBlockers.length > 0} fullWidth>
                    Place Order with ACH
                  </Button>
                </div>
              </div>
            )}
            </Card>
        </div>

        {/* Order Summary */}
        <aside className={styles.sidebar}>
          <Card className={styles.summaryCard}>
            <h2>Order Summary</h2>
            {product.lifestyleImageUrl && (
              <div className={styles.lifestyleImageWrapper}>
                <img loading="lazy" src={product.lifestyleImageUrl} alt="" className={styles.lifestyleImage} />
              </div>
            )}
            <div className={styles.productInfo}>
              <span className={styles.productName}>{product.name}</span>
              {isDosageSelectable
                ? dosage && <span className={styles.dosageInfo}>{dosage.amount}</span>
                : <span className={styles.dosageInfo} role="note">{DOCTOR_NOW_DOSAGE_NOTICE}</span>}
            </div>
            
            {/* Supply Duration Display */}
            <div className={styles.supplyDuration}>
              <span className={styles.supplyLabel}>{getSupplyLabel(selectedSupplyMonths)} Supply</span>
              {selectedTerm.internalMonths > 1 && (
                <span className={styles.supplyCalc}>{formatRate(supplyRate)}/mo × {selectedTerm.internalMonths} months</span>
              )}
            </div>

            {/* Fulfillment Schedule Info (cold-chain/cooler products) */}
            {product.maxSupplyMonths && selectedSupplyMonths > (product.maxSupplyMonths || 12) && (() => {
              const max = product.maxSupplyMonths || 12;
              const full = Math.floor(selectedSupplyMonths / max);
              const rem = selectedSupplyMonths % max;
              const total = full + (rem > 0 ? 1 : 0);
              const words = [, 'One', 'Two', 'Three', 'Four', 'Five', 'Six'];
              const desc = rem > 0
                ? `${words[total] || total} shipments (${full}x ${max}-month + 1x ${rem}-month)`
                : `${words[total] || total} ${max}-month shipments`;
              return (
                <div className={styles.fulfillmentInfo}>
                  <span className={styles.fulfillmentIcon}>&#x1F4E6;</span>
                  <div>
                    <span className={styles.fulfillmentLabel}>Fulfillment: {desc}</span>
                    <span className={styles.fulfillmentNote}>Paid in full today &mdash; shipped per schedule</span>
                  </div>
                </div>
              );
            })()}

            {/* Asynchronous review — what replaces the booking step for a DrCare request. The
                wording is imported from web/utils/asyncConsultCopy.js and is NEVER retyped here;
                four surfaces render these same sentences and they drifted when each owned a copy. */}
            {!canOfferScheduling && (
              <div className={styles.asyncReviewNotice} data-async-review>
                <span className={styles.asyncReviewTitle}>{NO_APPOINTMENT_NEEDED_NOTICE}</span>
                <ul className={styles.asyncReviewList}>
                  <li>{ASYNC_REVIEW_NOTICE}</li>
                  <li>{APPROVAL_UPDATE_NOTICE}</li>
                  <li>{CONTACT_IF_QUESTIONS_NOTICE}</li>
                </ul>
              </div>
            )}

            {/* Appointment Time — rendered ONLY when scheduling is genuinely required. The gate used
                to be the NEGATIVE form (show unless this is a DoctorNow product that opts out), which
                let any product carrying a slot in router state paint an appointment row. The positive
                predicate is strictly stronger and keeps the row ABSENT FROM THE DOM otherwise. */}
            {canOfferScheduling && appointmentTime && (
              <div className={styles.appointmentDisplay}>
                <span className={styles.appointmentLabel}>Appointment</span>
                <span className={styles.appointmentTime}>
                  {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][new Date(appointmentTime.utcTime).getDay()]}, {appointmentTime.date} at {appointmentTime.time} {appointmentTime.timezone}
                </span>
              </div>
            )}

            {/* Dosage Selection — hidden for DoctorNow (provider metadata, card 54405) or single dosage */}
            {isDosageSelectable && product.dosages?.filter(d => d.isActive !== false)?.length > 1 && (
              <DosageSelector
                dosages={product.dosages.filter(d => d.isActive !== false)}
                selectedDosage={dosage}
                onSelect={handleDosageSelect}
                selectedSupplyMonths={selectedSupplyMonths}
                tierType={pricing?.tierType}
                hasTierPriceModifier={hasTierPriceModifier}
                defaultView="expanded"
                className={styles.dosageSelector}
              />
            )}

            {/* Supply Duration Selector */}
            <div className={styles.supplySelector}>
              <label className={styles.supplySelectorLabel}>Select Supply Duration</label>
              <div className={styles.supplyCards}>
                {(() => {
                  const meta = dosage?.supplyPricingMeta || {};
                  const months = hasTieredPricing
                    ? [...new Set([1, 3, 6, 12, ...Object.keys(displaySupplyPricing).map(Number)].filter(n => {
                        const m = meta[String(n)];
                        return n > 0 && displaySupplyPricing[String(n)] !== undefined && (m?.enabled !== false);
                      }))].sort((a, b) => a - b)
                    : [1, 3];
                  return months.map((n) => {
                    const moPrice = hasTieredPricing && displaySupplyPricing[String(n)]
                      ? extractTierPrice(displaySupplyPricing[String(n)]) : (dosage?.price || 0);
                    const displayPrice = systemPromo?.applicableMonths?.[String(n)]
                      ? Math.round(moPrice * (1 - systemPromo.applicableMonths[String(n)] / 100))
                      : moPrice;
                    // moRate feeds the badge (pre-promo, symmetric with basePrice); displayRate is
                    // what the card shows (post-promo). Both are per internal month.
                    const displayRate = toPerMonthRate(displayPrice, String(n));
                    // `dosage?.` — not `dosage.` — this runs for every card, and a product with no
                    // priceable dosage leaves `dosage` null (the chain guarded supplyPricing, not dosage).
                    // Savings baseline: the "1" key sells a 60 Day term, so both sides of the
                    // comparison are taken per INTERNAL month. Against the raw stored value the
                    // baseline reads double and inflates every badge on the page.
                    const basePrice = toPerMonthRate(extractTierPrice(displaySupplyPricing?.['1']), '1')
                      || dosage?.price || 0;
                    const moRate = toPerMonthRate(moPrice, String(n));
                    const savePct = n > 1 && basePrice > 0 && moRate < basePrice
                      ? Math.round((1 - moRate / basePrice) * 100) : 0;
                    const label = meta[String(n)]?.label;
                    const isSelected = (selectedSupplyMonths || 1) === n;
                    return (
                      <button
                        key={n}
                        type="button"
                        className={`${styles.supplyCard} ${isSelected ? styles.supplyCardActive : ''}`}
                        onClick={() => { setQuantity(n); setSelectedSupplyMonths(n); }}
                      >
                        {savePct > 0 && <span className={styles.supplyCardBadge}>Save {savePct}%</span>}
                        <span className={styles.supplyCardDuration}>{label || getSupplyLabel(n)}</span>
                        <span className={styles.supplyCardPrice}>{formatRate(displayRate)}/mo</span>
                        {/* Total stays stored × raw key — the charge-exact amount. Deriving it as
                            rate × internalMonths drifts a cent on odd-cent rows (16 in catalog). */}
                        <span className={styles.supplyCardTotal}>{formatRate(displayPrice * n)} total</span>
                      </button>
                    );
                  });
                })()}
              </div>
            </div>
            <div className={styles.promoSection}>
              <label className={styles.promoLabel}>Promo Code</label>
              <div className={styles.promoInputWrapper}>
                <input
                  type="text"
                  value={promoCode}
                  onChange={handlePromoChange}
                  placeholder="Enter code"
                  className={`${styles.promoInput} ${promoValidation.status === 'valid' ? styles.promoInputValid : ''} ${promoValidation.status === 'invalid' ? styles.promoInputError : ''}`}
                />
                {promoValidation.status === 'validating' && (
                  <span className={styles.promoStatus}>
                    <span className={styles.promoSpinner}></span>
                  </span>
                )}
                {promoValidation.status === 'valid' && (
                  <span className={`${styles.promoStatus} ${styles.promoValid}`}>&#10003;</span>
                )}
                {promoValidation.status === 'invalid' && (
                  <span className={`${styles.promoStatus} ${styles.promoInvalid}`}>&#10007;</span>
                )}
              </div>
              {promoValidation.status === 'valid' && promoValidation.data && (
                <span className={styles.promoSuccess}>
                  {promoValidation.data.displayText || (
                    promoValidation.data.discountType === 'free_months'
                      ? `${promoValidation.data.freeMonths} month${promoValidation.data.freeMonths > 1 ? 's' : ''} free`
                      : promoValidation.data.discountType === 'percentage'
                        ? `${promoValidation.data.discountValue}% off`
                        : `${formatRate(promoValidation.data.discountValue)} off`
                  )}
                </span>
              )}
              {promoValidation.status === 'invalid' && (
                <span className={styles.promoError}>Invalid or expired code</span>
              )}
            </div>
            {creditBalanceCents > 0 && (
              <div className={styles.creditSection}>
                <label className={styles.creditToggle}>
                  <input
                    type="checkbox"
                    checked={applyCredit}
                    onChange={(e) => setApplyCredit(e.target.checked)}
                    className={styles.creditCheckbox}
                  />
                  <span className={styles.creditToggleText}>
                    <span>Apply account credit</span>
                    <span className={styles.creditBalance}>{formatPrice(creditBalanceCents)} available</span>
                  </span>
                </label>
              </div>
            )}
            <div className={styles.summaryItems}>
              <div className={styles.summaryItem}>
                <span>Medication Price</span>
                <div className={styles.priceCompare}>
                  {dosage?.retailPrice && dosage.retailPrice > supplyRate && (
                    <span className={styles.retailPriceStrike}>{formatRate(dosage.retailPrice)}</span>
                  )}
                  <span className={dosage?.retailPrice && dosage.retailPrice > supplyRate ? styles.memberPrice : ''}>{formatRate(supplyRate)}/mo</span>
                </div>
              </div>
              <div className={styles.summaryItem}>
                <span>Subscription Duration</span>
                <span>{getSupplyLabel(selectedSupplyMonths)}</span>
              </div>
              <div className={styles.summaryItem}>
                <span>Medication ({getSupplyLabel(selectedSupplyMonths)})</span>
                <span>{formatPrice(supplyTotal)}</span>
              </div>
              {systemPromoDiscount.breakdown.length > 0 && (
                <>
                  {systemPromoDiscount.breakdown.map(({ month, pct, amount }) => (
                    <div key={month} className={`${styles.summaryItem} ${styles.promoDiscountItem}`}>
                      <span>Month {month}: {pct}% off</span>
                      <span className={styles.promoDiscountAmount}>-{formatPrice(amount)}</span>
                    </div>
                  ))}
                </>
              )}
              {pricing?.discount > 0 && (
                <div className={`${styles.summaryItem} ${styles.promoDiscountItem}`}>
                  <span>
                    Promo Discount{pricing.promoCode ? ` (${pricing.promoCode})` : ''}{pricing.discountType === 'free_months'
                      ? ` — ${pricing.freeMonths === 1 ? 'first month' : `first ${pricing.freeMonths} months`} free`
                      : ''}
                  </span>
                  <div className={styles.priceCompare}>
                    <span className={styles.promoOriginalPrice}>{formatPrice(supplyTotal)}</span>
                    <span className={styles.promoDiscountAmount}>-{formatPrice(pricing.discount)}</span>
                  </div>
                </div>
              )}
              {achPromoApplied && achDiscount > 0 && (
                <div className={`${styles.summaryItem} ${styles.promoDiscountItem}`}>
                  <span>ACH First-Time Bonus</span>
                  <span className={styles.promoDiscountAmount}>-{formatPrice(achDiscount)}</span>
                </div>
              )}
              {appliedCreditsCents > 0 && (
                <div className={`${styles.summaryItem} ${styles.promoDiscountItem}`}>
                  <span>Account Credit</span>
                  <span className={styles.promoDiscountAmount}>-{formatPrice(appliedCreditsCents)}</span>
                </div>
              )}
              {/* Membership Benefits */}
              {(pb?.consultationFee?.status !== 'none' || pricing?.consultationFee > 0 || pricing?.tierSavings?.consultationFeeWaived > 0 || isConsultationWaived) && (
                <div className={styles.summaryItem}>
                  <span>{isTierHold ? 'Consultation Hold' : 'Consultation Fee'}</span>
                  {isConsultationWaived ? (
                    <span className={styles.includedWithTier}>
                      <span className={styles.includedStrikePrice}>{formatPrice(pb?.consultationFee?.rawAmount || product?.consultationFee || 3000)}</span>
                      Included
                    </span>
                  ) : pb?.consultationFee?.status === 'included' ? (
                    <span className={styles.includedWithTier}>
                      <span className={styles.includedStrikePrice}>{formatPrice(pb.consultationFee.rawAmount)}</span>
                      Included
                    </span>
                  ) : isTierHold ? (
                    <span className={styles.holdPriceGroup}>
                      <span>{formatPrice(heldConsultationFee)}</span>
                      <span className={styles.holdBadge} onClick={() => setShowHoldInfoModal(true)}>On Hold</span>
                    </span>
                  ) : (
                    <span>{formatPrice(pb?.consultationFee?.amount ?? pricing?.consultationFee ?? 0)}</span>
                  )}
                </div>
              )}
              {isTierHold && (
                <div className={styles.holdExplainer}>
                  Charged only if prescription is not approved
                </div>
              )}
              <div className={styles.summaryItem}>
                <span>Shipping & Handling</span>
                {isShippingWaived ? (
                  <span className={styles.includedWithTier}>
                    <span className={styles.includedStrikePrice}>{formatPrice(pb?.shippingFee?.rawAmount || product?.shippingCost || 999)}</span>
                    Included
                  </span>
                ) : pb?.shippingFee?.status === 'included' ? (
                  <span className={styles.includedWithTier}>
                    <span className={styles.includedStrikePrice}>{formatPrice(pb.shippingFee.rawAmount)}</span>
                    Included
                  </span>
                ) : (
                  <span>{(pb?.shippingFee?.amount ?? pricing?.shippingCost ?? 0) > 0 ? formatPrice(pb?.shippingFee?.amount ?? pricing?.shippingCost) : 'FREE'}</span>
                )}
              </div>
              {pricing?.tax > 0 && (
                <div className={styles.summaryItem}>
                  <span>Tax</span>
                  <span>{formatPrice(pricing?.tax)}</span>
                </div>
              )}
              {increasedStrengthFeeCents > 0 && (
                <div className={styles.summaryItem}>
                  <span>
                    {pricing?.increasedStrengthFee?.label || 'Increased Strength Fee'}
                    {strengthFeeMultiplier > 1 ? ` (×${strengthFeeMultiplier})` : ''}
                  </span>
                  <span>{formatPrice(increasedStrengthFeeCents)}</span>
                </div>
              )}
            </div>
            {pricingError && (
              <div className={styles.pricingErrorBanner}>
                {pricingError}
              </div>
            )}
            <div className={styles.totalDueToday}>
              <span>Total Due Today</span>
              <div className={styles.totalPriceGroup}>
                {(achPromoApplied && achDiscount > 0) && (
                  <span className={styles.originalPrice}>{formatPrice(totalDueCents)}</span>
                )}
                <span className={(achPromoApplied && achDiscount > 0) ? styles.discountedTotal : ''}>
                  {formatPrice(totalDueCents - achDiscount)}
                </span>
              </div>
            </div>
            {/* Estimated Savings — visually separated from total */}
            {(() => {
              // Card 6216 — count the retail saving ONCE. Do NOT re-add the engine's
              // `visualSavingsOnly`: it is defined as `retail * months - medicationAfterAllDiscounts`,
              // which expands to `retailSavings + totalDiscounts`, and BOTH halves are already summed
              // here (as `retailSavings`, and inside `lifetimeSavingsContribution`). Adding it counted
              // the retail saving twice and the promo discounts three times, so the headline exceeded
              // the itemised rows printed directly beneath it.
              const totalSavings = (pb?.lifetimeSavingsContribution ?? systemPromoDiscount.total) + savingsVsOneMonth + retailSavings;
              return totalSavings > 0 ? (
                <div className={styles.estimatedSavings}>
                  <div className={styles.estimatedSavingsHeader}>
                    <span className={styles.estimatedSavingsTitle}>Estimated Savings</span>
                    <span className={styles.estimatedSavingsSubtext}>vs. retail pricing</span>
                  </div>
                  <div className={styles.estimatedSavingsAmount}>
                    <span>You Save</span>
                    <span>{formatRate(totalSavings)}</span>
                  </div>
                  {savingsVsOneMonth > 0 && (
                    <div className={styles.estimatedSavingsDetail}>
                      <span>Multi-month discount ({savingsPct}% off)</span>
                      <span>-{formatRate(savingsVsOneMonth)}</span>
                    </div>
                  )}
                  {dosage?.retailPrice && dosage.retailPrice > supplyRate && (
                    <div className={styles.estimatedSavingsDetail}>
                      <span>vs. retail price ({retailSavingsPct}% off)</span>
                      <span>-{formatRate(retailSavings)}</span>
                    </div>
                  )}
                </div>
              ) : null;
            })()}
          </Card>
        </aside>
      </div>

      {/* Subscription Clause Modal */}
      <SubscriptionClauseModal
        isOpen={isSubscriptionModalOpen}
        onClose={() => setIsSubscriptionModalOpen(false)}
        onSubmit={(consentData) => {
          setSubscriptionClauseAgreed(true);
          if (consentData) setSubscriptionConsentData(consentData);
          setIsSubscriptionModalOpen(false);
        }}
        membershipTier={
          pricing?.membership?.monthlyRateCents != null
            ? { name: pricing.membership.tierName || 'Your membership', monthlyPrice: pricing.membership.monthlyRateCents / 100 }
            : membershipTier
        }
        supplyMonths={selectedSupplyMonths || 1}
        userName={user?.profile ? `${user.profile.firstName || ""} ${user.profile.lastName || ""}`.trim() : ""}
        onSignatureCapture={handleModalSignatureCapture}
        initialSignatureData={getCurrentSignatureData()}
      />

      {/* Payment Decline Modal */}
      <PaymentDeclineModal
        isOpen={showDeclineModal}
        onClose={() => setShowDeclineModal(false)}
        errorCode={paymentError?.code}
        errorMessage={paymentError?.message}
        onRetry={handleRetryPayment}
        onTryDifferentCard={handleTryDifferentCard}
        onContactSupport={handleContactSupport}
      />

      {/* Hold Info Modal */}
      {showHoldInfoModal && (
        <div className={styles.holdModalOverlay} onClick={() => setShowHoldInfoModal(false)}>
          <div className={styles.holdModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.holdModalHeader}>
              <h3 className={styles.holdModalTitle}>Consultation Fee - On Hold</h3>
              <button className={styles.holdModalClose} onClick={() => setShowHoldInfoModal(false)} aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className={styles.holdModalBody}>
              <p>Your consultation fee is included with approved prescriptions only. If your prescription request is approved, this fee will be waived. If denied, this fee will be charged.</p>
              <p>Please ensure you are requesting medication that is medically necessary and appropriate for you.</p>
            </div>
            <div className={styles.holdModalFooter}>
              <button className={styles.holdModalBtn} onClick={() => setShowHoldInfoModal(false)}>Got it</button>
            </div>
          </div>
        </div>
      )}

      {/* ACH Promotion Modal */}
      <AchPromotionModal
        isOpen={showAchPromoModal}
        onClose={handleAchPromoDecline}
        onAccept={handleAchPromoAccept}
        savingsAmount={(pricing?.unitPrice || 0) / 100}
        productName={product?.name || 'your treatment'}
      />
    </div>
  );
};

export default CheckoutPage;
